import { cacheDecorator } from '@ds/swymkit/tools'

import { searchFetch } from '~/services/fetch-search'

import { conversationSearchParser } from '~/entities/container/conversation/api/parser-conversation'
import { userInfoParser, userInfoParserWithLogin } from '~/entities/user/user/api/parser-search-user'

import { getPlatformId, isOnPremise } from '~/features-shared/config-manager/stores/swym-config'
import type { IFedSearchInfo, IFedSearchResponse } from '~/features-shared/member/interfaces/search'

import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'

export interface ISearchParams {
  /**
   * The search string that will be queried
   */
  text: string
  /**
   * The login of the current user
   */
  userLogin: string
  /**
   * Should the usersGroups be included in the results
   */
  usersGroup?: boolean
}

/**
 * Creates the query to be sent to conversation
 * @param query the text typed by the user in the left col search input
 * @returns the query to be sent to fed search
 */
export function buildQueryForConvSearchInLeftCol(text: string | undefined) {
  const SWYM_DIRECT_MESSAGE_PREDICATE = 'swym:DirectMessage'
  const CONV_MEMBERS_FED_SEARCH_PREFIX = 'userConversations'
  return [
    `([ds6w:type]:"${SWYM_DIRECT_MESSAGE_PREDICATE}" AND `,
    ((text || '').trim()
      .split(' ')
      .map(x => (`([ds6w:label]:(${x}) OR ${CONV_MEMBERS_FED_SEARCH_PREFIX}: (${x}))`))
      .join(' AND ')),
    ')',
  ].join('')
}

/**
 * Search for users.
 *
 * @info Use this when you want to search only users, and want their login in the response
 *
 * @param params The {@link ISearchParams} used for search
 * @returns Search results
 */
async function _getUserInfoWithLogin(params: ISearchParams) {
  const tenantId = getPlatformId()
  const source = ['swym']

  // cache this request to avoid spamming the server with useless request
  const response = await cacheDecorator(
    `search-user-info-login-${params.text}-${params.userLogin}`,
    () => searchFetch
      .post('/search', {
        json: {
          nresults: 10,
          query: '[ds6w:type]:(\"pno:Person\")',
          source,
          specific_source_parameter: {
            swym: {
              // use of Swym be prefix handler to search for user or groups,
              // then search for groups if not found in the user group index
              additional_query: ` AND personOrGroup:(${params.text})`,
              agoraMode: true,
            },
          },
          select_predicate: ['ds6w:label', 'ds6w:type', 'login'],
          label: `SocialUserMention-${params.userLogin}-${new Date()
            .getTime()}`,
          locale: 'en',
          start: 0,
          with_synthesis: false,
          with_synthesis_attribute: false,
          tenant: tenantId,
        },
      })
      .json<IFedSearchResponse>(),
    2 * 60 * 1000, // 2 min
  )

  return {
    total: response.infos.nhits,
    data: userInfoParserWithLogin(response),
  }
}

export const SearchService = {
  async getUserInfo(params: ISearchParams) {
    const tenantId = getPlatformId()
    const source = ['swym']

    // Add usersGroup if configured
    if (params.usersGroup)
      source.push('usersgroup')

    // cache this request to avoid spamming the server with useless request
    const response = await cacheDecorator(
      `search-user-info-${params.text}-${params.userLogin}`,
      () => searchFetch
        .post('/search', {
          json: {
            nresults: 20,
            query: '[ds6w:type]:(\"foaf:Group\" OR \"pno:Person\")',
            source,
            // verifier si le service users groups existe
            // .concat(
            //   usersGroupsServiceIsAvailable === true
            //     ? [ 'usersgroup' ]
            //     : []
            // ),
            specific_source_parameter: {
              usersgroup: {
                additional_query:
                  `AND [ds6w:label]:(${params.text.trim()}*)`,
              },
              swym: {
                // use of Swym be prefix handler to search for user or groups,
                // then search for groups if not found in the user group index
                additional_query: ` AND personOrGroup:(${params.text})`,
                agoraMode: true,
              },
            },
            select_predicate: ['ds6w:label', 'ds6w:type'],
            label: `SocialUserMention-${params.userLogin}-${new Date()
              .getTime()}`,
            locale: 'en',
            start: 0,
            with_synthesis: false,
            with_synthesis_attribute: false,
            tenant: tenantId,
          },
        })
        .json<IFedSearchResponse>(),
      60000,
    )

    return {
      total: response.infos.nhits,
      data: userInfoParser(response) as unknown as IFedSearchResponse,
    }
  },

  getUserInfoWithLogin: mutualizerDecorator<typeof _getUserInfoWithLogin, Parameters<typeof _getUserInfoWithLogin>[0]>({
    fetch: _getUserInfoWithLogin,
    merge: (bucketList) => {
      const uniqueNames = new Set(bucketList.map(b => b.text))
      const text = Array.from(uniqueNames)
        .map(b => `"${b}"`)
        .join(' OR ')

      return {
        ...bucketList.pop()!, // useful to keep the limit
        text,
      }
    },
    resolve([response, [_option, resolve]]) {
      resolve(response)
    },
    options: {
      throttle: 300,
      limit: 10,
    },
  }),

  async getUsersWithResourceId(params: ISearchParams) {
    const tenantId = getPlatformId()
    const source = ['swym']

    // Add usersGroup if configured
    if (params.usersGroup)
      source.push('usersgroup')

    const response: IFedSearchResponse = await searchFetch
      .post('/search', {
        json: {
          nresults: 20,
          query: '[ds6w:type]:(\"foaf:Group\" OR \"pno:Person\")',
          source,
          order_by: 'desc',
          order_field: 'relevance',
          specific_source_parameter: {
            usersgroup: {
              additional_query:
                `AND ([ds6w:label]:(${params.text.trim()}*) OR [ds6w:label]:"${params.text.trim()}")`,
            },
            swym: {
              // use of Swym be prefix handler to search for user or groups, then search for groups if not found in the user group index
              additional_query: ` AND personOrGroup:(${params.text})`,
              agoraMode: true,
            },
          },
          select_predicate: ['ds6w:label', 'ds6w:type', 'ds6w:resourceUid'],
          label: `3DSwym-${params.userLogin}-${new Date()
            .getTime()}`,
          locale: 'en',
          start: 0,
          with_synthesis: false,
          with_synthesis_attribute: false,
          tenant: tenantId,
        },
      })
      .json()

    return {
      total: response.infos.nhits,
      data: userInfoParser(response),
    }
  },
  async getUsersAndConversations(params: ISearchParams) {
    const tenantId = getPlatformId()
    const response: IFedSearchResponse = await searchFetch
      .post('/search', {
        json: {
          query: `([ds6w:type]:"swym:DirectMessage" AND (${params.text}) AND (NOT dm_type:private)) OR ((topicConversations:(${params.text})) AND (dm_type:private)) OR (personOrGroup:(${params.text}))`,
          select_predicate: ['ds6w:type', 'ds6w:label', 'title', 'id', 'resourceid'],
          tenant: tenantId,
          nresults: 50,
          reset: true,
          label: `3DSwym-${params.userLogin}-${new Date()
            .getTime()}`,
          with_synthesis: false,
          with_synthesis_attribute: false,
          source: ['swym'],
        },
      })
      .json()
    return {
      data: userInfoParser(response, true) as unknown as IFedSearchResponse,
    }
  },
  /** Fedsearch for conversation search in left column */
  async getConversations(params: ISearchParams) {
    const tenantId = getPlatformId()
    const response: IFedSearchResponse = await searchFetch
      .post('/search', {
        json: {
          query: buildQueryForConvSearchInLeftCol(params?.text), // Get swym/RT conversations with given search text
          select_predicate: ['ds6w:type', 'ds6w:label', 'resourceid'],
          tenant: tenantId,
          nresults: 100,
          order_field: 'ds6w:modified',
          order_by: 'desc',
          label: `conversation-search-${new Date()
            .getTime()}`,
          with_synthesis: false,
          source: ['swym', '3dmessaging'],
        },
      })
      .json()
    return {
      data: conversationSearchParser(response),
    }
  },

  // A separate method to include 'login' predicate
  async getUsersAndConversationsWithLogin(params: ISearchParams) {
    const tenantId = getPlatformId()
    const response: IFedSearchResponse = await searchFetch
      .post('/search', {
        json: {
          query: `([ds6w:type]:"swym:DirectMessage" AND (${params.text}) AND (NOT dm_type:private)) OR ((topicConversations:(${params.text})) AND (dm_type:private)) OR (personOrGroup:(${params.text}))`,
          select_predicate: ['ds6w:type', 'ds6w:label', 'title', 'id', 'resourceid', 'login'],
          tenant: tenantId,
          nresults: 50,
          reset: true,
          label: `3DSwym-${params.userLogin}-${new Date()
            .getTime()}`,
          with_synthesis: false,
          with_synthesis_attribute: false,
          source: ['swym'],
        },
      })
      .json()
    return {
      data: userInfoParserWithLogin(response) as unknown as IFedSearchResponse,
    }
  },
  /**
   * Retrieve media information from fedsearch using idshasMember
   * @param query Containing all ids of media
   * @returns
   */
  async getMediaFromFedSearch(query: string) {
    const tenantId = getPlatformId()
    const response = await searchFetch
      .post('/search', {
        json: {
          with_indexing_date: true,
          with_synthesis: true,
          with_nls: false,
          label: '3DSearch-skd1',
          locale: 'en',
          with_synthesis_hierarchical: true,
          // select_file: [ 'icon', 'thumbnail_2d' ],
          query,
          refine: {},
          order_by: 'desc',
          order_field: 'relevance',
          nresults: 40,
          start: '0',
          source: ['swym', '3dspace', 'drive', 'dashboard'],
          tenant: tenantId,
        },
      })
      .json<{ results: any }>()

    return response.results
  },
  async getUserGroup(groupName: string) {
    const tenantId = getPlatformId()
    const response = await searchFetch
      .post('/search', {
        json: {
          with_synthesis: false,
          with_nls: false,
          label: groupName,
          with_synthesis_hierarchical: false,
          query: `[ds6w:type]:"foaf:Group" AND [ds6w:label]:"${groupName}"`,
          source: [
            'usersgroup',
          ],
          tenant: tenantId,
        },
      })
      .json<{ results: any[] }>()

    const results = response.results
    if (Array.isArray(results))
      return results.some(result => result && result.attributes && result.attributes.some(attr => ((attr.name === 'ds6w:label') && (attr.value === groupName))))

    return false
  },
  async isUserGroupMember(UID: string, groupName: string) {
    let formattedID = ''
    /**
     * We have to format of UID :
     * - the new one : 00000000_xxxx_0000_xxxx_000000000000
     * - the old one: iam:xxx
     *
     * When calling the search we get all the member by there UID but in a different format
     * - the new one : <uuid:00000000_xxxx_0000_xxxx_000000000000>
     * - the old one : <http://iam.3ds.com/xxx>
     */
    if (UID.includes('iam'))
      formattedID = `<http://iam.3ds.com/${UID.split('iam:')[1]}>`
    else formattedID = `<uuid:${UID}>`

    const tenantId = getPlatformId()
    const response = await searchFetch
      .post('/search', {
        json: {
          with_synthesis: false,
          with_nls: false,
          label: '3Dswym-usergroup-search-member',
          select_predicate: ['foaf:member'],
          with_synthesis_hierarchical: false,
          query: `[ds6w:type]:"foaf:Group" AND [ds6w:label]:"${groupName}"`,
          source: [
            'usersgroup',
          ],
          tenant: tenantId,
        },
      })
      .json<{ results: any[] }>()

    const results = response.results
    if (Array.isArray(results))
      return results.some(result => result && result.attributes && result.attributes.some(attr => ((attr.name === 'foaf:member') && (attr.value === formattedID))))

    return false
  },
  async getContentsFromFedSearch(subject_uri: string, query: string, page = 0) {
    const tenantId = getPlatformId()
    const response = await searchFetch
      .post('/search', {
        json: {
          with_synthesis: false,
          with_nls: false,
          with_synthesis_hierarchical: false,
          label: `3DSearch-${subject_uri}-${query}`,
          query: `((${query}) AND [ds6w:containerUid]: (\"${subject_uri}\"))`,
          order_by: 'desc',
          order_field: 'ds6w:created',
          nresults: 50 + (page * 50),
          start: 0 + (page * 50),
          source: ['swym'],
          tenant: tenantId,
        },
      })
      .json<{ results: any }>()

    return response.results
  },

  /**
   * Get content information from fedsearch using resourceUid
   *
   * @info Use this when you want to get the type of item
   *
   * @param subjectUri subjectUri for search
   * @returns Search results
   */
  async getContentByResource(subjectUri: string) {
    const tenantId = getPlatformId()
    const source = ['swym']
    // OP doest not have RTConversation, Thus skip adding '3dmessaging' in source to avoid fedsearch error
    if (!isOnPremise())
      source.push('3dmessaging')

    return cacheDecorator(
      `content-type-${subjectUri}`,
      () => searchFetch
        .post('/search', {
          json: {
            with_synthesis: false,
            with_nls: false,
            select_predicate: ['ds6w:type'],
            with_synthesis_hierarchical: false,
            label: `3DSearch-swym-container-type-${subjectUri}`,
            query: `[ds6w:resourceUid]: "${subjectUri}"`,
            order_by: 'desc',
            order_field: 'ds6w:created',
            source,
            tenant: tenantId,
          },
        })
        .json<IFedSearchResponse>(),
    )
  },

}

/**
 * Retrieve content information from fedsearch.
 * @param query Query containing contents ids to search.
 */
async function _get3DXContentsFromFedSearch(query: string): Promise<IFedSearchInfo | IFedSearchInfo[]> {
  const tenantId = getPlatformId()
  const response = await searchFetch
    .post('/search', {
      json: {
        with_synthesis: false,
        with_nls: false,
        with_synthesis_hierarchical: false,
        label: `3DSearch-${Date.now()}`,
        query: `[ds6w:resourceUid]: (${query})`,
        source: ['swym', '3dspace', 'drive', 'dashboard'],
        tenant: tenantId,
      },
    })
    .json<IFedSearchResponse>()

  return userInfoParser(response) as IFedSearchInfo | IFedSearchInfo[]
}

/**
 * This query is built for searching 3xdcontent using federated search.
 * Calls will be mutualized.
 * @param option Content id.
 */
export const get3DXContentsFromFedSearchMutualized = mutualizerDecorator<
  typeof _get3DXContentsFromFedSearch,
  Parameters<typeof _get3DXContentsFromFedSearch>[0]
>({
  fetch: _get3DXContentsFromFedSearch,
  merge: (bucketlist) => {
    const bucketListQuoted = bucketlist.map(id => `"${id}"`)
    const contentIds = bucketListQuoted.join(' OR ')
    return contentIds
  },
  resolve: ([response, [option, resolve]]) => {
    const result = (response as IFedSearchInfo[])
      .find(el => el.resource_id === option) as IFedSearchInfo

    resolve(result)
  },
})
